import { Controller } from "@hotwired/stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import { Markdown } from "@tiptap/markdown"
import { TableKit } from "@tiptap/extension-table"
import { Image } from "@tiptap/extension-image"
import { TaskItem, TaskList } from "@tiptap/extension-list"

export default class extends Controller {
  static targets = ["field", "host", "label", "commands", "command", "mode"]

  connect() {
    this.hostTarget.replaceChildren()

    this.editor = new Editor({
      element: this.hostTarget,
      extensions: [StarterKit, TableKit, Image, TaskList, TaskItem, Markdown],
      editorProps: {
        attributes: {
          class: "prose max-w-none w-full min-h-32 focus:outline-none [&_li>p]:inline",
          "aria-label": this.labelTarget.textContent.trim()
        }
      },
      content: this.fieldTarget.value,
      contentType: "markdown",
      onUpdate: () => {
        this.fieldTarget.value = this.editor.getMarkdown()
      },
      onTransaction: () => {
        this.refreshCommands()
      }
    })

    this.showVisual()

    this.focusEditor = this.focusEditor.bind(this)
    this.labelTarget.addEventListener("click", this.focusEditor)
  }

  disconnect() {
    this.labelTarget.removeEventListener("click", this.focusEditor)
    this.editor.destroy()
    this.hostTarget.replaceChildren()
    this.hostTarget.classList.add("hidden")
    this.fieldTarget.classList.remove("hidden")
  }

  switchMode(event) {
    const mode = event.currentTarget.dataset.mode
    if (mode === this.mode) return

    if (mode === "markdown") {
      this.fieldTarget.value = this.editor.getMarkdown()
      this.showMarkdown()
    } else {
      this.editor.commands.setContent(this.fieldTarget.value, { contentType: "markdown" })
      this.showVisual()
    }
  }

  runCommand(event) {
    const button = event.currentTarget
    const chain = this.editor.chain().focus()
    const commandArguments = button.dataset.commandArguments

    chain[button.dataset.command](commandArguments ? JSON.parse(commandArguments) : undefined).run()
  }

  focusEditor() {
    if (this.mode === "markdown") {
      this.fieldTarget.focus()
    } else {
      this.editor.commands.focus()
    }
  }

  showVisual() {
    this.mode = "visual"
    this.hostTarget.classList.remove("hidden")
    this.commandsTarget.classList.remove("hidden")
    this.fieldTarget.classList.add("hidden")
    this.highlightMode()
    this.refreshCommands()
  }

  showMarkdown() {
    this.mode = "markdown"
    this.hostTarget.classList.add("hidden")
    this.commandsTarget.classList.add("hidden")
    this.fieldTarget.classList.remove("hidden")
    this.highlightMode()
  }

  highlightMode() {
    this.modeTargets.forEach((button) => {
      const selected = button.dataset.mode === this.mode
      button.classList.toggle("btn-primary", selected)
      button.classList.toggle("btn-text", !selected)
      button.setAttribute("aria-selected", selected)
    })
  }

  refreshCommands() {
    this.commandTargets.forEach((button) => {
      const name = button.dataset.activeName
      if (!name) return

      const commandArguments = button.dataset.commandArguments
      button.classList.toggle("btn-active", this.editor.isActive(name, commandArguments ? JSON.parse(commandArguments) : undefined))
    })
  }
}
